import { useState } from "react";
import axios from "../axios";
import { toast } from "react-toastify";

export const useOffers = () => {
    const [offerData, setOfferData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedOffers, setSelectedOffers] = useState({
        offerValidity: null,
        delivery: null,
        payment: null,
        incoterm: null,
        packing: null,
        warranty: null,
        forceMajeure: null
    });

    // Fetch offer data from API
    const fetchOffers = async () => {
        setLoading(true);
        try {
            const response = await axios.get("/project/get-offer-options");
            setOfferData(response.data);
            return response.data;
        } catch (error) {
            console.error("Error fetching offers:", error);
            toast.error("Failed to fetch offer data");
            return null;
        } finally {
            setLoading(false);
        }
    };

    // Restore saved offer selections from the offer IDs stored on the project
    const loadProjectOffers = async (project) => {
        try {
            const savedIds = {
                offerValidity: project?.offerValidity ?? null,
                delivery: project?.delivery ?? null,
                payment: project?.payment ?? null,
                incoterm: project?.incoterm ?? null,
                packing: project?.packing ?? null,
                warranty: project?.warranty ?? null,
                forceMajeure: project?.forceMajeure ?? null
            };

            const emptySelection = {
                offerValidity: null,
                delivery: null,
                payment: null,
                incoterm: null,
                packing: null,
                warranty: null,
                forceMajeure: null
            };

            if (!Object.values(savedIds).some((id) => id != null)) {
                setSelectedOffers(emptySelection);
                return null;
            }

            const data = offerData || (await fetchOffers());
            if (!data) return null;

            const groupLabels = {
                offerValidity: "Offer Validity",
                delivery: "Delivery",
                payment: "Payment",
                incoterm: "Incoterm",
                packing: "Packing",
                warranty: "Warranty",
                forceMajeure: "Force Majeure"
            };

            const offers = { ...emptySelection };
            Object.entries(savedIds).forEach(([key, id]) => {
                if (id != null) {
                    offers[key] = (data[groupLabels[key]] || []).find((option) => option.offerId === id) || null;
                }
            });

            setSelectedOffers(offers);
            return offers;
        } catch (error) {
            console.error("Error loading project offers:", error);
            return null;
        }
    };

    // Build summary string from selected offers
    const buildOfferSummary = (offers) => {
        const parts = [];
        
        if (offers?.offerValidity) {
            parts.push(`Offer Validity: ${offers.offerValidity.offerValue}`);
        }
        if (offers?.delivery) {
            parts.push(`Delivery: ${offers.delivery.offerValue}`);
        }
        if (offers?.payment) {
            parts.push(`Payment: ${offers.payment.offerValue}`);
        }
        if (offers?.incoterm) {
            parts.push(`Incoterm: ${offers.incoterm.offerValue}`);
        }
        if (offers?.packing) {
            parts.push(`Packing: ${offers.packing.offerValue}`);
        }
        if (offers?.warranty) {
            parts.push(`Warranty: ${offers.warranty.offerValue}`);
        }
        if (offers?.forceMajeure) {
            parts.push(`Force Majeure: ${offers.forceMajeure.offerValue}`);
        }

        return parts.join(" | ");
    };

    // Helper to get offer IDs for saving with project.
    // Keys must match the MaicoProject properties so they bind on the backend.
    const getOfferIds = (offers) => {
        return {
            offerValidity: offers?.offerValidity?.offerId ?? null,
            delivery: offers?.delivery?.offerId ?? null,
            payment: offers?.payment?.offerId ?? null,
            incoterm: offers?.incoterm?.offerId ?? null,
            packing: offers?.packing?.offerId ?? null,
            warranty: offers?.warranty?.offerId ?? null,
            forceMajeure: offers?.forceMajeure?.offerId ?? null
        };
    };

    return {
        offerData,
        loading,
        selectedOffers,
        setSelectedOffers,
        fetchOffers,
        loadProjectOffers,
        buildOfferSummary,
        getOfferIds
    };
};